import { BudgetActions, BudgetPermissions } from 'hooks'
import { RefObject } from 'react'
import {
  BaseBudget,
  BudgetNotificationSettings,
  BudgetWithIds,
  DiscountType,
  Event,
} from 'typings'

export type BudgetContext = {
  originalValue: RefObject<BudgetWithIds | undefined>
  permission: BudgetPermissions
  action: RefObject<BudgetActions>
}

type TrackBudgetChangeParams = {
  event: Event
  injectedPayload?: Record<string, any>
  formValues: any
  budgetContext: BudgetContext
  discountType: DiscountType | undefined
  track: any
}

export const trackBudgetChange = ({
  event,
  injectedPayload,
  formValues,
  budgetContext,
  discountType,
  track,
}: TrackBudgetChangeParams) => {
  const { originalValue, permission, action } = budgetContext
  const prevValue = originalValue.current?.amount ?? 0
  const newValue = (formValues.restrictions.budget as BaseBudget)?.amount
  const utilized =
    (formValues.restrictions.budget as BaseBudget)?.utilized ??
    originalValue.current?.utilized
  const payload = {
    permission,
    action: action.current,
    prevValue: prevValue || 'Empty',
    newValue: newValue || 'Empty',
    utilized: utilized || 0,
    promotionId: formValues.id || 'New Promotion',
    promotionType: formValues.type,
    benefitType: formValues[formValues.type]?.benefit.type || 'Not applicable',
    discountType,
    isGTUtilized: newValue > utilized,
    isGTPrevValue: newValue > prevValue,
    ...injectedPayload,
  }
  track(event, payload)
}

type TrackDrawerViewParams = {
  config: BudgetWithIds | { amount?: number; utilized?: number }
  track: any
}

export const trackBudgetDrawerView = ({
  config,
  track,
}: TrackDrawerViewParams) => {
  const payload = {
    ...config,
    amount: config.amount ?? 'Empty',
    utilized: config.utilized ?? 0,
  }
  track(Event.BUDGET_DRAWER_VIEW, payload)
}

export type BudgetValues = {
  initialValue: number | undefined
  currentValue: number | undefined
  utilized: number | undefined
  initialNotification: BudgetNotificationSettings | undefined
  notification: BudgetNotificationSettings | undefined
}

type TrackDrawerActionParams = {
  event: Event.BUDGET_DRAWER_CONFIRM_CLICK | Event.BUDGET_DRAWER_CANCEL_CLICK
  budgetValues: BudgetValues
  track: any
}

export const trackBudgetDrawerAction = ({
  event,
  budgetValues,
  track,
}: TrackDrawerActionParams) => {
  const {
    initialValue,
    currentValue,
    utilized,
    initialNotification,
    notification,
  } = budgetValues
  const payload = {
    initialValue: initialValue ?? 'Empty',
    currentValue: currentValue ?? 'Empty',
    utilized: utilized ?? 0,
    initialNotification,
    notification,
    isGTUtilized: (currentValue ?? 0) > (utilized ?? 0),
    isGTPrevValue: (currentValue ?? 0) > (initialValue ?? 0),
  }
  track(event, payload)
}

export type PromotionInfo = {
  promotionId?: string
  promotionType?: string
  benefitType?: string
  discountType?: string
}

type TrackBudgetViewParams = {
  promotionInfo: PromotionInfo
  viewState: string
  track: any
}

export const trackBudgetView = ({
  promotionInfo,
  viewState,
  track,
}: TrackBudgetViewParams) => {
  const { promotionId, promotionType, benefitType, discountType } =
    promotionInfo
  const payload = {
    promotionId: promotionId || 'New Promotion',
    promotionType: promotionType || 'Empty',
    benefitType: benefitType || 'Not applicable',
    discountType: discountType || 'Empty',
    viewState,
  }
  track(Event.BUDGET_VIEW, payload)
}
